<template>
  <div class="assignment-detail-page">
    <!-- Header with just Viva logo -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="studentName"
        :is-assignment="true"
        current-portal="assignment"
      />
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="viva-container">
      <div class="viva-section">
        <div class="viva-spinner-section">
          <i class="fa fa-spinner fa-spin viva-spinner"></i>
          <p class="viva-spinner-text">Loading assignment details...</p>
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="viva-container">
      <div class="viva-section">
        <div class="viva-alert viva-alert-danger">
          <p>{{ error }}</p>
        </div>
        <button class="viva-btn viva-btn-orange viva-btn-block" @click="loadAssignment">
          Try Again
        </button>
      </div>
    </div>

    <!-- Assignment Details -->
    <div v-else class="viva-container">
      <div class="viva-section assignment-detail-section">
        <h2>{{ assignment.title }}</h2>

        <!-- Course and instructor info -->
        <div class="assignment-meta">
          <p><strong>Course:</strong> {{ assignment.course_title }}</p>
          <p><strong>Instructor:</strong> {{ assignment.instructor_name }}</p>
          <p v-if="assignment.due_date">
            <strong>Due:</strong> {{ formatDate(assignment.due_date) }}
          </p>
        </div>

        <!-- Instructions -->
        <div class="assignment-instructions">
          <h3>Instructions</h3>
          <div class="instructions-content">{{ assignment.instructions }}</div>
        </div>

        <!-- Assignment Materials Download (for teacher uploads) -->
        <div v-if="assignment.assigned_file_url" class="assignment-materials">
          <h3>Assignment Materials</h3>
          <a
            :href="serverAddr + assignment.assigned_file_url"
            target="_blank"
            rel="noopener noreferrer"
            class="download-materials-btn"
          >
            <i class="fa fa-download"></i>
            Download Assignment Materials
          </a>
        </div>

        <!-- Status indicator -->
        <div v-if="studentStatus.status === 'completed'" class="viva-alert viva-alert-success">
          <i class="fa fa-check-circle"></i>
          <span>You completed this assignment on {{ formatDate(studentStatus.submission_date) }}</span>
        </div>

        <!-- Start link - only show for available assignments, not completed -->
        <a
          v-if="studentStatus.can_start && studentStatus.status !== 'completed'"
          :href="vivaUploadUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="viva-btn viva-btn-orange viva-btn-block"
        >
          <i class="fa fa-play-circle"></i>
          Start my VIVA
        </a>

        <!-- Not available message -->
        <div v-if="!studentStatus.can_start && studentStatus.status === 'not_started'" class="viva-alert viva-alert-danger">
          <p>This assignment is not yet available.</p>
        </div>
      </div>
    </div>

    <!-- Back Link -->
    <div class="back-section">
      <a @click="backToAssignments" class="back-link" style="cursor: pointer;">
        <span>←</span> Back to ASSIGNMENTS
      </a>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import { handleApiError } from '../utils/errorHandler.js';
import env from '../utils/env.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';

export default {
  name: 'StudentAssignmentDetail',
  components: {
    ProfileDropdown
  },

  data() {
    return {
      assignment: null,
      studentStatus: {
        status: 'not_started',
        can_start: false
      },
      loading: true,
      error: null,
      studentName: ''
    };
  },

  async mounted() {
    await this.loadAssignment();
    await this.fetchStudentName();
  },

  computed: {
    serverAddr() {
      return env.serverAddr;
    },
    vivaUploadUrl() {
      return `${env.serverAddr}/viva/upload/?vparam=${this.studentStatus.viva_unique_id}`;
    }
  },

  methods: {
    fetchStudentName() {
      const statusData = getStatusData();
      if (statusData && statusData.student_name !== undefined) {
        this.studentName = statusData.student_name;
      }
    },

    async loadAssignment() {
      this.loading = true;
      this.error = null;

      try {
        const assignmentId = this.$route.params.id;

        const response = await apiClient.get(
          `/api/viva/student/assignment/${assignmentId}/`
        );

        if (response.data) {
          this.assignment = response.data.assignment;
          this.studentStatus = response.data.student_status;
        }
      } catch (error) {
        console.error('Failed to load assignment:', error);
        this.error = handleApiError(error, this.$router, 'Failed to load assignment details.');
      } finally {
        this.loading = false;
      }
    },

    backToAssignments() {
      // Set loading immediately when back button is clicked
      this.$root.$emit('show-loading');
      // Get course ID from assignment data
      this.$router.push({
        name: 'student-course-assignments',
        params: { id: this.assignment.course_id }
      });
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.assignment-detail-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
  padding-bottom: 50px;
}

.assignment-detail-page .viva-container {
  padding-top: 40px;
}

.assignment-detail-section {
  max-width: 800px;
}

.assignment-detail-section h2 {
  font-size: 32px;
  font-weight: 700;
  color: var(--viva-dark);
  margin-bottom: 20px;
}

.assignment-meta {
  background: var(--viva-white);
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  border: 2px solid var(--viva-cream);
}

.assignment-meta p {
  margin: 8px 0;
  color: var(--viva-grey);
  font-size: 16px;
}

.assignment-meta strong {
  color: var(--viva-dark);
  margin-right: 8px;
}

.assignment-instructions {
  margin: 30px 0;
}

.assignment-instructions h3 {
  color: var(--viva-dark);
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
}

.instructions-content {
  background: var(--viva-white);
  border-left: 4px solid var(--viva-orange);
  padding: 20px;
  border-radius: 4px;
  line-height: 1.8;
  color: var(--viva-grey);
  font-size: 16px;
  white-space: pre-wrap;
}

.assignment-materials {
  background: var(--viva-white);
  border-left: 4px solid var(--viva-green);
  padding: 20px;
  border-radius: 4px;
  margin: 30px 0;
}

.assignment-materials h3 {
  color: var(--viva-dark);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 10px 0;
}

.download-materials-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--viva-green);
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
  transition: color 0.2s;
}

.download-materials-btn:hover {
  color: var(--viva-dark);
  text-decoration: underline;
}

.download-materials-btn i {
  font-size: 16px;
}

.viva-alert {
  padding: 15px 20px;
  border-radius: 8px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.viva-alert i {
  font-size: 20px;
}

.viva-alert-success {
  background-color: rgba(128, 194, 151, 0.2);
  color: var(--viva-green);
  border: 2px solid var(--viva-green);
}

.viva-alert-danger {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: 2px solid #dc3545;
}

.viva-btn-block {
  width: 100%;
  max-width: 400px;
  margin: 30px auto;
  display: block;
  font-size: 20px;
  padding: 16px 32px;
}

.viva-spinner-section {
  text-align: center;
  padding: 60px 20px;
}

.viva-spinner {
  font-size: 48px;
  color: var(--viva-orange);
  margin-bottom: 20px;
}

.viva-spinner-text {
  color: var(--viva-grey);
  font-size: 18px;
}
</style>
